import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  GraduationCap, 
  Search, 
  Plus,
  Edit,
  Trash2,
  X,
  Calendar,
  Users,
  Clock,
  CheckCircle,
  Eye,
  Filter,
  FileText,
  FileSpreadsheet,
  Download,
  RefreshCcw,
  LayoutGrid,
  Shield,
  Layers,
  PlayCircle,
  XCircle,
  DollarSign,
  Upload,
  ChevronDown,
  ChevronUp,
  FileOutput,
  Briefcase,
  List,
  LayoutList,
  Check,
  MapPin,
} from 'lucide-react';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import FacturationListPageLayout, {
  facturationTableScrollClass,
} from '../facturation/components/FacturationListPageLayout';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import {
  buildHrColumnVisibilityState,
  buildHrQawaemColumnDefs,
  buildHrImportExportProps,
  buildHrControlsStrip,
  isHrColumnVisible,
  createHrHandleToggleColumn,
  hrTableColSpan,
  useHrModalBodyLock,
  HrFormModalTabBar,
  useHrTableRowSelection,
  HrBulkDeleteFilterAction,
  buildHrEmployeeSelectOptions,
  formatHrEmployeeLabel,
  useHrEmployees,
  sliceHrListItems,
} from './hrListPageUtils';
import { useHrConfig } from './useHrConfig';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import ar from 'date-fns/locale/ar-SA';
import 'react-datepicker/dist/react-datepicker.css';
import { useHR } from '../../core/contexts/HRContext';
import localApiAdapter from '../../core/services/localApiAdapter';
import { formatBrandMoney, getBrandCurrencySuffix, getBrandLocale, getBrandDatePickerLocale } from '../../shared/utils/brandCurrency';
import { generateReportPDF } from '../../shared/utils/reportPdfGenerator';
import { generateReportExcel } from '../../shared/utils/reportExcelGeneratorAdvanced';
import { CustomSelect, CustomDatePicker, CustomNumberInput } from '../../shared/components/custom';
import { exportToCSV, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';
registerLocale('ar', ar);

const HR_TABLE_COLUMNS = [
  { key: 'training', labelKey: 'col_training' },
  { key: 'trainer', labelKey: 'col_trainer' },
  { key: 'date', labelKey: 'col_date' },
  { key: 'duration', labelKey: 'col_duration' },
  { key: 'participants', labelKey: 'col_participants' },
  { key: 'cost', labelKey: 'col_cost' },
  { key: 'status', labelKey: 'col_status' },
  { key: 'actions', labelKey: 'col_actions', required: true },
];

const TrainingPage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.ressources_humaines.pages.trainingPage.${key}`, opts);
  const qawaem = isSaudiBrand();
  const { isDashboardLightMode } = useGlobalTheme();
  const dateLocale = getBrandLocale(i18n.language);
  const datePickerLocale = getBrandDatePickerLocale();
  const hrConfig = useHrConfig();

  // Use HR Context for real data
  const { 
    trainingSessions: trainings, 
    addTrainingSession, 
    updateTrainingSession, 
    removeTrainingSession, 
    refreshData,
    loading 
  } = useHR();
  
  const { employees: hrEmployees } = useHrEmployees();
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingTraining, setEditingTraining] = useState(null);
  const [viewingTraining, setViewingTraining] = useState(null);
  useHrModalBodyLock(showModal, showViewModal);
  const [selectedTrainings, setSelectedTrainings] = useState([]);
  const [viewMode, setViewMode] = useState('table');
  // Main view mode state
  const [mainViewMode, setMainViewMode] = useState('all_trainings');
  const [quickFilter, setQuickFilter] = useState('all');
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  // Advanced options toggle
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  
  // Export states
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);

  // Dropdown states and refs
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  // Categories for smart dropdown
  const [categories, setCategories] = useState([]);
  
  // Default training categories
  const defaultCategories = [
    p('cat_hrdf'),
    p('cat_saudization'),
    p('cat_bilingual'),
    p('cat_security'),
    p('cat_management'),
    p('cat_technical'),
    p('cat_it'),
    p('cat_commercial'),
    p('cat_accounting'),
    p('cat_hr'),
    p('cat_quality'),
    p('cat_communication'),
    p('cat_soft_skills'),
    p('cat_leadership'),
    p('cat_languages'),
  ];
  
  const [formTab, setFormTab] = useState('general');
  

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    trainer: '',
    startDate: '',
    endDate: '',
    duration: '',
    participants: [],
    status: 'planifie',
    category: '',
    cost: '',
    location: '',
    hrdfSupported: false
  });

  const triggerRefresh = () => refreshData();

  // Calculate stats
  const stats = {
    total: trainings.length,
    planifie: trainings.filter(t => t.status === 'planifie').length,
    en_cours: trainings.filter(t => t.status === 'en_cours').length,
    termine: trainings.filter(t => t.status === 'termine').length,
    totalCost: trainings.reduce((sum, t) => sum + parseFloat(t.cost || 0), 0),
    totalParticipants: trainings.reduce((sum, t) => sum + t.participants.length, 0)
  };

  // NEW: Calculate training distribution
  const trainingDistribution = {
    all_trainings: {
      title: p('dist_overview'),
      data: [
        { filterKey: 'planifie', name: p('dist_planifie'), count: stats.planifie, color: "bg-gradient-to-r from-orange-500 to-orange-600" },
        { filterKey: 'en_cours', name: p('dist_en_cours'), count: stats.en_cours, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { filterKey: 'termine', name: p('dist_termine'), count: stats.termine, color: "bg-gradient-to-r from-green-500 to-green-600" }
      ]
    },
    par_statut: {
      title: p('dist_by_status_title'),
      data: [
        { filterKey: 'planifie', name: p('dist_planifie'), count: stats.planifie, color: "bg-gradient-to-r from-orange-500 to-orange-600" },
        { filterKey: 'en_cours', name: p('dist_en_cours'), count: stats.en_cours, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { filterKey: 'termine', name: p('dist_termine'), count: stats.termine, color: "bg-gradient-to-r from-green-500 to-green-600" }
      ]
    },
    par_categorie: {
      title: p('dist_by_category_title'),
      data: [...new Set(trainings.map(t => t.category))].map(category => ({
        filterKey: category,
        name: category,
        count: trainings.filter(t => t.category === category).length,
        color: "bg-gradient-to-r from-purple-500 to-purple-600"
      }))
    },
    par_date: {
      title: p('dist_by_month_title'),
      data: (() => {
        const monthCounts = {};
        trainings.forEach(t => {
          const month = new Date(t.startDate).toLocaleDateString(dateLocale, { year: 'numeric', month: 'long' });
          monthCounts[month] = (monthCounts[month] || 0) + 1;
        });
        return Object.entries(monthCounts).map(([month, count]) => ({
          filterKey: month,
          name: month,
          count,
          color: "bg-gradient-to-r from-indigo-500 to-indigo-600"
        }));
      })()
    }
  };

  const distributionInfo = trainingDistribution[mainViewMode] || trainingDistribution.all_trainings;

  // NEW: Main view tabs
  const mainTabs = [
    { id: 'all_trainings', name: p('tab_general'), icon: LayoutGrid },
    { id: 'par_statut', name: p('tab_by_status'), icon: Shield },
    { id: 'par_categorie', name: p('tab_by_category'), icon: Layers },
    { id: 'par_date', name: p('tab_by_date'), icon: Calendar }
  ];

  const quickFilterButtons = [
    { id: 'all', name: p('filter_all_f'), icon: GraduationCap },
    { id: 'planifie', name: p('filter_planifie'), icon: Calendar },
    { id: 'en_cours', name: p('filter_en_cours'), icon: PlayCircle },
    { id: 'termine', name: p('filter_termine'), icon: CheckCircle }
  ];

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) {
        setShowImportDropdown(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Extract unique categories from trainings + merge with defaults
  useEffect(() => {
    const existingCategories = trainings
      .map(t => t.category)
      .filter(c => c && c.trim() !== '');
    
    // Merge defaults with existing (remove duplicates)
    const allCategories = [...new Set([...defaultCategories, ...existingCategories])];
    setCategories(allCategories);
  }, [trainings]);

  const baseFilteredTrainings = useMemo(() => trainings.filter((training) => {
    const matchesSearch = !searchTerm ||
      training.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      training.trainer?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      training.category?.toLowerCase().includes(searchTerm.toLowerCase());
    if (!matchesSearch) return false;

    if (mainViewMode === 'all_trainings' && quickFilter !== 'all' && training.status !== quickFilter) {
      return false;
    }

    if (activeDistributionFilter) {
      switch (mainViewMode) {
        case 'all_trainings':
        case 'par_statut':
          if (training.status !== activeDistributionFilter) return false;
          break;
        case 'par_categorie':
          if (training.category !== activeDistributionFilter) return false;
          break;
        case 'par_date': {
          const month = training.startDate
            ? new Date(training.startDate).toLocaleDateString(dateLocale, { year: 'numeric', month: 'long' })
            : '';
          if (month !== activeDistributionFilter) return false;
          break;
        }
        default:
          break;
      }
    }

    return true;
  }), [trainings, searchTerm, quickFilter, mainViewMode, activeDistributionFilter, dateLocale]);

  const filteredTrainings = useMemo(
    () => sliceHrListItems(baseFilteredTrainings, hrConfig.itemsPerPage),
    [baseFilteredTrainings, hrConfig.itemsPerPage],
  );

  const { allRowsSelected, someRowsSelected, handleSelectAll, toggleRecord: handleSelectRecord } =
    useHrTableRowSelection(filteredTrainings, selectedTrainings, setSelectedTrainings);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title?.trim()) {
      alert(p('validation_title_required', { defaultValue: 'عنوان التدريب مطلوب' }));
      return;
    }
    try {
    if (editingTraining) {
        await updateTrainingSession(editingTraining.id, formData);
        notifyUpdated(showNotify, p('entity_label', { defaultValue: 'التدريب' }));
    } else {
        await addTrainingSession(formData);
        notifyCreated(showNotify, p('entity_label', { defaultValue: 'التدريب' }));
    }
    resetForm();
    } catch (error) {
      console.error('[TrainingPage] save error:', error);
      notifyError(showNotify, p('save_error_training'));
    }
  };

  const handleEdit = (training) => {
    setEditingTraining(training);
    setFormData(training);
    setFormTab('general');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        await removeTrainingSession(id);
        notifyDeleted(showNotify, p('entity_label', { defaultValue: 'التدريب' }));
      } catch (error) {
        console.error('Error deleting training:', error);
        notifyError(showNotify, p('alert_delete_error'));
      }
    }
  };

  const handleView = (training) => {
    setViewingTraining(training);
    setShowViewModal(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      trainer: '',
      startDate: '',
      endDate: '',
      duration: '',
      participants: [],
      status: 'planifie',
      category: '',
      cost: '',
      location: '',
      hrdfSupported: false
    });
    setEditingTraining(null);
    setFormTab('general');
    setShowModal(false);
  };

  const openNewTrainingModal = () => {
    setEditingTraining(null);
    setFormData({
      title: '',
      description: '',
      trainer: '',
      startDate: '',
      endDate: '',
      duration: '',
      participants: [],
      status: 'planifie',
      category: '',
      cost: '',
      location: '',
      hrdfSupported: false
    });
    setFormTab('general');
    setShowModal(true);
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('bulk_delete_confirm', { count: selectedTrainings.length }))) {
      try {
        const deleteCount = selectedTrainings.length;
        await Promise.all(selectedTrainings.map(id => removeTrainingSession(id)));
        notifyBulkDeleted(showNotify, deleteCount, p('entity_label_plural', { defaultValue: 'تدريب' }));
      setSelectedTrainings([]);
      } catch (error) {
        console.error('Error bulk deleting:', error);
        notifyError(showNotify, p('alert_delete_error'));
      }
    }
  };

  const statusOptions = [
    { value: 'planifie', label: p('status_planifie'), icon: Calendar, style: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
    { value: 'en_cours', label: p('status_en_cours'), icon: PlayCircle, style: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    { value: 'termine', label: p('status_termine'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'annule', label: p('status_annule'), icon: XCircle, style: 'bg-red-500/20 text-red-400 border-red-500/30' }
  ];

  const getStatusLabel = (status) => {
    const opt = statusOptions.find(s => s.value === status);
    return opt ? opt.label : p('em_dash');
  };

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await localApiAdapter.put(`/hr/training/${recordId}`, { status: newStatus });      setViewingTraining((prev) =>
        prev && prev.id === recordId ? { ...prev, status: newStatus } : prev
      );
      await refreshData();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const getStatusBadge = (status) => {
    const config = statusOptions.find((s) => s.value === status) || statusOptions[0];
    const { label, icon: Icon, style } = config;
    return (
      <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${style}`}>
        <Icon size={14} />
        {label}
      </span>
    );
  };


  const qawaemStatusOptions = useMemo(
    () => [{ value: 'all', label: p('filter_all') }, ...statusOptions.map((s) => ({ value: s.value, label: s.label }))],
    [p],
  );
  const listStats = useMemo(() => [
    { key: 'total', label: p('stat_total'), value: stats.total, icon: GraduationCap, variant: 'copper' },
    { key: 'planned', label: p('stat_planifie'), value: stats.planifie, icon: Calendar, variant: 'forest' },
    { key: 'ongoing', label: p('stat_en_cours'), value: stats.en_cours, icon: PlayCircle, variant: 'copper' },
    { key: 'done', label: p('stat_termine'), value: stats.termine, icon: CheckCircle, variant: 'forest' },
  ], [stats, p]);
  const [columnVisibility, setColumnVisibility] = useState(() =>
    buildHrColumnVisibilityState(HR_TABLE_COLUMNS),
  );
  const qawaemColumns = useMemo(
    () => buildHrQawaemColumnDefs(HR_TABLE_COLUMNS, p, columnVisibility),
    [p, columnVisibility],
  );
  const handleToggleColumn = createHrHandleToggleColumn(setColumnVisibility, HR_TABLE_COLUMNS);
  const qawaemVisibleColumnCount = qawaemColumns.filter((c) => c.checked !== false).length;
  const isColVisible = (key) => isHrColumnVisible(columnVisibility, key);
  const tableColSpan = hrTableColSpan(columnVisibility);
  const trainingFormTabs = useMemo(() => ([
    { id: 'general', label: p('section_basic'), icon: GraduationCap, iconClassName: 'text-blue-400' },
    { id: 'logisticsInfo', label: p('section_logistics'), icon: MapPin, iconClassName: 'text-amber-400' },
  ]), [p]);

  const advancedOptionsContent = (
    <div className="space-y-6 pt-0">
{/* Main View Tabs */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20 shadow-xl shadow-black/10">
        <div className="flex space-x-1 overflow-x-auto table-scroll">
          {mainTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setMainViewMode(tab.id);
                setQuickFilter('all');
                setActiveDistributionFilter(null);
              }}
              className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                mainViewMode === tab.id
                  ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg'
                  : 'text-white/80 hover:bg-white/10 hover:text-white'
              }`}
            >
              <tab.icon className="h-5 w-5" />
              <span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Quick Filter Buttons */}
      {mainViewMode === 'all_trainings' && (
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20 shadow-xl shadow-black/10">
          <div className="flex space-x-1 overflow-x-auto table-scroll">
            {quickFilterButtons.map((filter) => (
              <button
                key={filter.id}
                onClick={() => {
                  setQuickFilter(filter.id);
                  setActiveDistributionFilter(null);
                }}
                className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                  quickFilter === filter.id
                    ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg'
                    : 'text-white/80 hover:bg-white/10 hover:text-white'
                }`}
              >
                <filter.icon className="h-5 w-5" />
                <span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Training Distribution */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 shadow-xl shadow-black/10"
      >
        <h3 className="text-xl font-semibold text-white mb-4">
          {distributionInfo.title}
        </h3>
        <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '3')}>{distributionInfo.data.map((item) => (
            <button
              key={item.filterKey || item.name}
              type="button"
              onClick={() => setActiveDistributionFilter(activeDistributionFilter === item.filterKey ? null : item.filterKey)}
              className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left w-full ${
                activeDistributionFilter === item.filterKey
                  ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-orange-500/20'
                  : 'border-white/10 hover:border-white/20 hover:bg-white/10'
              }`}
            >
              <div className="flex justify-between items-center mb-2">
                <h4 className={`font-medium ${activeDistributionFilter === item.filterKey ? 'text-white' : 'text-white/80'}`}>{item.name}</h4>
                <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>
                  {item.count}
                </div>
              </div>
              <div className="w-full bg-white/10 h-3 rounded-full">
                <div 
                  className={`h-3 rounded-full ${item.color}`}
                  style={{ width: `${(() => {
                    const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                    return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                  })()}%` }}
                ></div>
              </div>
              <p className={`mt-2 text-sm ${activeDistributionFilter === item.filterKey ? 'text-white/80' : 'text-white/60'}`}>
                {p('percent_of_items', { percent: (() => {
                  const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                  return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                })() })}
              </p>
            </button>
          ))}
        </div>
      </motion.div>
    </div>
  );


  const handleExportPDF = async () => {
    try {
      setIsGeneratingPDF(true);
      const reportData = filteredTrainings.map(t => ({
        [p('export_col_title')]: t.title,
        [p('export_col_trainer')]: t.trainer,
        [p('export_col_category')]: t.category,
        [p('col_participants')]: t.participants?.length ?? 0,
        [p('col_duration')]: t.duration,
        [p('export_col_cost')]: formatBrandMoney(t.cost),
        [p('export_col_status')]: getStatusLabel(t.status),
        [p('col_date')]: `${new Date(t.startDate).toLocaleDateString(dateLocale)} - ${new Date(t.endDate).toLocaleDateString(dateLocale)}`
      }));

      await generateReportPDF(p('report_title'), reportData, { orientation: 'landscape' });
    } catch (error) {
      console.error('[TrainingPage] PDF export error:', error);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setIsGeneratingExcel(true);
      const reportData = filteredTrainings.map(t => ({
        [p('export_col_title')]: t.title,
        [p('export_col_trainer')]: t.trainer,
        [p('export_col_category')]: t.category,
        [p('col_participants')]: t.participants?.length ?? 0,
        [p('col_duration')]: t.duration,
        [p('export_col_cost')]: parseFloat(t.cost || 0),
        [p('export_col_status')]: getStatusLabel(t.status),
        [p('col_start_date')]: new Date(t.startDate).toLocaleDateString(dateLocale),
        [p('col_end_date')]: new Date(t.endDate).toLocaleDateString(dateLocale)
      }));

      await generateReportExcel(p('report_title'), reportData);
    } catch (error) {
      console.error('[TrainingPage] Excel export error:', error);
    } finally {
      setIsGeneratingExcel(false);
    }
  };

  const handleExport = () => {
    const headers = [
      { key: 'title', label: p('export_col_title') },
      { key: 'trainer', label: p('col_trainer') },
      { key: 'category', label: p('col_category') },
      { key: 'startDate', label: p('col_start_date') },
      { key: 'endDate', label: p('col_end_date') },
      { key: 'duration', label: p('col_duration') },
      { key: 'participantsCount', label: p('col_participants') },
      { key: 'cost', label: p('col_cost') },
      { key: 'statusLabel', label: p('col_status') },
      { key: 'location', label: p('col_location') }
    ];
    const dataToExport = filteredTrainings.map((t) => ({
      ...t,
      participantsCount: t.participants?.length ?? 0,
      statusLabel: getStatusLabel(t.status),
      startDate: t.startDate ? new Date(t.startDate).toLocaleDateString(dateLocale) : '',
      endDate: t.endDate ? new Date(t.endDate).toLocaleDateString(dateLocale) : ''
    }));
    const fileName = `formations_export_${new Date().toISOString().split('T')[0]}.csv`;
    try {
      exportToCSV(dataToExport, headers, fileName);
    } catch (error) {
      console.error('[TrainingPage] CSV export error:', error);
      alert(p('export_error'));
    }
  };

  const handleImport = () => {
    const headers = [
      { key: 'title', label: p('export_col_title') },
      { key: 'trainer', label: p('col_trainer') },
      { key: 'startDate', label: p('col_start_date') },
      { key: 'endDate', label: p('col_end_date') },
      { key: 'duration', label: p('col_duration') },
      { key: 'category', label: p('col_category') },
      { key: 'cost', label: p('col_cost') },
      { key: 'location', label: p('col_location') },
      { key: 'status', label: p('col_status') },
      { key: 'description', label: p('col_description') }
    ];
    const schema = {
      requiredFields: ['title'],
      validators: {
        status: (value) => !value || ['planifie', 'en_cours', 'termine', 'annule'].includes(String(value).toLowerCase())
      }
    };
    triggerFileInput(async (file) => {
      try {
        const result = await importFromFile(file, headers, schema);
        if (result.success && result.data?.length) {
          let successCount = 0;
          for (const row of result.data) {
            try {
              const st = row.status ? String(row.status).toLowerCase() : 'planifie';
              const status = ['planifie', 'en_cours', 'termine', 'annule'].includes(st) ? st : 'planifie';
              await addTrainingSession({
                title: row.title,
                description: row.description || '',
                trainer: row.trainer || '',
                startDate: row.startDate || new Date().toISOString().split('T')[0],
                endDate: row.endDate || row.startDate || '',
                duration: row.duration || '',
                participants: [],
                status,
                category: row.category || '',
                cost: row.cost != null && row.cost !== '' ? String(row.cost) : '',
                location: row.location || ''
              });
              successCount++;
            } catch (err) {
              console.error('Import training row error:', err);
            }
          }
          await refreshData();
          alert(p('import_success', { count: successCount }) + (result.invalidCount ? ` (${result.invalidCount})` : ''));
        } else {
          alert(result.errors?.length ? result.errors.slice(0, 8).join('\n') : p('alert_import_error'));
        }
      } catch (error) {
        console.error('Import error:', error);
        alert(p('alert_import_error'));
      }
    });
  };

  const importExportProps = useMemo(() => buildHrImportExportProps({
    showImportDropdown, setShowImportDropdown, showExportDropdown, setShowExportDropdown,
    importDropdownRef, exportDropdownRef, p, t, isGeneratingPDF,
    handlers: { onImport: handleImport, onExportCSV: handleExport, onExportExcel: handleExportExcel, onExportPDF: handleExportPDF },
  }), [showImportDropdown, showExportDropdown, isGeneratingPDF, p, t]);

    return (
    <>
      <FacturationListPageLayout
        title={p('title')}
        subtitle={p('subtitle')}
        icon={GraduationCap}
        stats={listStats}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        filterActions={
          <HrBulkDeleteFilterAction
            count={selectedTrainings.length}
            onDelete={handleBulkDelete}
            label={p('delete_count', { count: selectedTrainings.length })}
          />
        }
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) })}
        columns={qawaemColumns}
        visibleColumnCount={qawaemVisibleColumnCount}
        totalColumnCount={qawaemColumns.length}
        onCreate={openNewTrainingModal}
        createLabel={p('new_item')}
        importExport={importExportProps}
        headerActions={<button type="button" onClick={triggerRefresh} className="refresh-button" title={p('refresh')}><RefreshCcw size={20} /></button>}
        showAdvancedOptions={showAdvancedOptions}
        onToggleAdvancedOptions={() => setShowAdvancedOptions(!showAdvancedOptions)}
        advancedOptionsLabel={p('advanced_options')}
        advancedOptionsContent={advancedOptionsContent}
        controlsStrip={buildHrControlsStrip({
          qawaem,
          viewMode,
          onViewModeChange: setViewMode,
          isDashboardLightMode,
          searchTerm,
          onSearchChange: setSearchTerm,
          searchPlaceholder: p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) }),
          cardsTitle: t('common.view_cards', { defaultValue: 'بطاقات' }),
          tableTitle: t('common.view_table', { defaultValue: 'جدول' }),
        })}
      >
        {viewMode === 'table' && (
          <div className={facturationTableScrollClass(qawaem)}>
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}><div className="flex justify-center"><QawaemTableCheckbox size="lg" checked={allRowsSelected} indeterminate={someRowsSelected} onChange={handleSelectAll} /></div></th>
                  {isColVisible('training') && <th className="px-6 py-4 text-left text-white font-semibold">{p('col_training')}</th>}
                  {isColVisible('trainer') && <th className="px-6 py-4 text-left text-white font-semibold">{p('col_trainer')}</th>}
                  {isColVisible('date') && <th className="px-6 py-4 text-left text-white font-semibold">{p('col_date')}</th>}
                  {isColVisible('duration') && <th className="px-6 py-4 text-left text-white font-semibold">{p('col_duration')}</th>}
                  {isColVisible('participants') && <th className="px-6 py-4 text-left text-white font-semibold">{p('col_participants')}</th>}
                  {isColVisible('cost') && <th className="px-6 py-4 text-left text-white font-semibold">{p('col_cost')}</th>}
                  {isColVisible('status') && <th className="px-6 py-4 text-center text-white font-semibold">{p('col_status')}</th>}
                  <QawaemTableHeaderActionsCell label={p('col_actions')} columns={qawaemColumns} onToggle={handleToggleColumn} visibleCount={qawaemVisibleColumnCount} totalCount={qawaemColumns.length} thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle" />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredTrainings.length > 0 ? (
                  filteredTrainings.map((training) => (
                    <tr key={training.id} className={`hover:bg-white/5 ${selectedTrainings.includes(training.id) ? 'bg-white/10' : ''}`}>
                      <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}><div className="flex justify-center"><QawaemTableCheckbox checked={selectedTrainings.includes(training.id)} onChange={() => handleSelectRecord(training.id)} /></div></td>
                      {isColVisible('training') && <td className="px-6 py-4 text-white font-semibold">{training.title}</td>}
                      {isColVisible('trainer') && <td className="px-6 py-4 text-white/80">{training.trainer || '—'}</td>}
                      {isColVisible('date') && (
                        <td className="px-6 py-4 text-white/80">
                          {training.startDate && !Number.isNaN(new Date(training.startDate).getTime())
                            ? new Date(training.startDate).toLocaleDateString(dateLocale)
                            : '—'}
                        </td>
                      )}
                      {isColVisible('duration') && <td className="px-6 py-4 text-white/80">{training.duration || '—'}</td>}
                      {isColVisible('participants') && <td className="px-6 py-4 text-white/80">{Array.isArray(training.participants) ? training.participants.length : 0}</td>}
                      {isColVisible('cost') && (
                        <td className="px-6 py-4 text-white/80">
                          {training.cost != null && training.cost !== '' ? formatBrandMoney(training.cost) : '—'}
                        </td>
                      )}
                      {isColVisible('status') && (
                        <td className="px-6 py-4 text-center align-middle">
                          <div className="flex justify-center">
                            <QawaemClickableStatusBadge status={training.status} recordId={training.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status')} />
                          </div>
                        </td>
                      )}
                      <td className="px-6 py-4 text-center align-middle">
                        <QawaemErpRowActions
                          onView={() => handleView(training)}
                          onEdit={() => handleEdit(training)}
                          onDelete={() => handleDelete(training.id)}
                          viewTitle={t('common.view')}
                          editTitle={t('common.edit')}
                          deleteTitle={t('common.delete')}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60"><GraduationCap size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty_title')}</p></td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {filteredTrainings.length > 0 ? (
              filteredTrainings.map((training, index) => (
                <motion.div
                  key={training.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-3 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <GraduationCap size={20} className="text-blue-400 shrink-0" />
                          <span className="truncate">{training.title}</span>
                        </h3>
                        <QawaemClickableStatusBadge status={training.status} recordId={training.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status')} />
                        {training.category ? (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-white/20 text-white/80">
                            {training.category}
                          </span>
                        ) : null}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_session')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div className="flex items-center gap-2">
                              <Briefcase size={16} className="text-blue-400 shrink-0" />
                              <span className="font-medium">{p('trainer_label')} :</span>{' '}
                              <span className="text-white">{training.trainer || '—'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Calendar size={16} className="text-violet-400 shrink-0" />
                              <span>
                                {new Date(training.startDate).toLocaleDateString(dateLocale)}
                                {training.startDate !== training.endDate
                                  ? ` → ${new Date(training.endDate).toLocaleDateString(dateLocale)}`
                                  : ''}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Clock size={16} className="text-white/50 shrink-0" />
                              <span>{training.duration || '—'}</span>
                            </div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_participants_budget')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div className="flex items-center gap-2">
                              <Users size={16} className="text-purple-400 shrink-0" />
                              <span>{p('participants_count', { count: training.participants?.length ?? 0 })}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <DollarSign size={16} className="text-amber-400 shrink-0" />
                              <span className="text-white">{formatBrandMoney(training.cost)}</span>
                            </div>
                          </div>
                        </div>
                        <div className="md:col-span-2">
                          <h5 className="font-medium text-white mb-2">{p('card_location_desc')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            {training.location ? (
                              <div className="text-white/90">{training.location}</div>
                            ) : (
                              <div className="text-white/50">{p('location_not_set')}</div>
                            )}
                            {training.description ? (
                              <p className="text-white/60 text-xs mt-2 line-clamp-3">{training.description}</p>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                    <QawaemErpRowActions
                      compact
                      onView={() => handleView(training)}
                      onEdit={() => handleEdit(training)}
                      onDelete={() => handleDelete(training.id)}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <GraduationCap size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty_title')}</p>
              </div>
            )}
          </div>
        )}
      </FacturationListPageLayout>

      {/* Add/Edit Modal */}
      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" style={{ top: 0, left: 0, right: 0, bottom: 0 }}>
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }} 
            animate={{ opacity: 1, scale: 1 }} 
            className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl max-h-[90vh] flex flex-col overflow-hidden" 
            style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }}
          >
            {/* Fixed Header */}
            <div className={`flex items-center justify-between p-6 border-b ${isDashboardLightMode ? 'border-gray-200' : 'border-white/10'}`}>
              <h2 className={`text-2xl font-bold ${isDashboardLightMode ? 'text-gray-900' : 'text-white'}`}>
                {editingTraining ? p('modal_edit') : p('modal_new')}
              </h2>
              <div className="flex items-center gap-3">
                <button 
                  type="submit" 
                  form="training-form" 
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"
                >
                  <CheckCircle size={18} />
                  {editingTraining ? p('update') : p('create')}
                </button>
                <button onClick={resetForm} className={isDashboardLightMode ? 'text-gray-500 hover:text-gray-800 transition-colors' : 'text-white/70 hover:text-white transition-colors'}>
                  <X size={24} />
                </button>
              </div>
            </div>
            
            
            <HrFormModalTabBar
              tabs={trainingFormTabs}
              activeTab={formTab}
              onTabChange={setFormTab}
              isLight={isDashboardLightMode}
              ariaLabel={editingTraining ? p('modal_edit') : p('modal_new')}
            />

            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="training-form" onSubmit={handleSubmit} >
                {formTab === 'general' && (
                  <>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                              <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_title')} <span className="qawaem-required-mark">*</span></label>
                              <input 
                                type="text" 
                                required 
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                                placeholder={p('placeholder_title')} 
                              />
                            </div>
                            <div>
                              <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_trainer')}</label>
                              <input 
                                type="text" 
                                value={formData.trainer}
                                onChange={(e) => setFormData({ ...formData, trainer: e.target.value })}
                                className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                                placeholder={p('placeholder_trainer')} 
                              />
                            </div>
                            <div>
                              <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_category')}</label>
                              <CustomSelect
                                value={formData.category}
                                onChange={(value) => setFormData({ ...formData, category: value })}
                                options={categories.map(cat => ({
                                  value: cat,
                                  label: cat
                                }))}
                                placeholder={p('placeholder_category')}
                                searchable={true}
                                creatable={true}
                              />
                              <p className="text-white/30 text-xs mt-1">
                                {p('categories_hint', { count: categories.length })}
                              </p>
                            </div>
                            <div>
                              <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_status')}</label>
                              <CustomSelect
                                value={formData.status}
                                onChange={(value) => setFormData({ ...formData, status: value })}
                                options={[
                                  { value: 'planifie', label: p('status_planifie') },
                                  { value: 'en_cours', label: p('status_en_cours') },
                                  { value: 'termine', label: p('status_termine') },
                                  { value: 'annule', label: p('status_annule') }
                                ]}
                                placeholder={p('placeholder_status')}
                              />
                            </div>
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_description')}</label>
                            <textarea 
                              value={formData.description}
                              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none"
                              rows={3} 
                              placeholder={p('placeholder_description')} 
                            />
                    </div>
                  </>
                )}
                {formTab === 'general' && (
                  <>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                      <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_start_date')} <span className="qawaem-required-mark">*</span></label>
                            <CustomDatePicker
                              value={formData.startDate}
                              onChange={(value) => setFormData({ ...formData, startDate: value })}
                              placeholder={p('placeholder_date')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_end_date')}</label>
                            <CustomDatePicker
                              value={formData.endDate}
                              onChange={(value) => setFormData({ ...formData, endDate: value })}
                              placeholder={p('placeholder_date')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_duration')}</label>
                            <input 
                              type="text" 
                              value={formData.duration}
                              onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                              placeholder={p('placeholder_duration')} 
                            />
                          </div>
                    </div>
                  </>
                )}
                {formTab === 'logisticsInfo' && (
                  <>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_cost')} ({getBrandCurrencySuffix()})</label>
                            <CustomNumberInput
                              value={formData.cost}
                              onChange={(value) => setFormData({ ...formData, cost: value })}
                              placeholder="0"
                              min={0}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_location')}</label>
                            <input 
                              type="text" 
                              value={formData.location}
                              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                              placeholder={p('placeholder_location')} 
                            />
                          </div>
                          <div className="md:col-span-2 flex items-center gap-3">
                            <input
                              type="checkbox"
                              id="hrdf-supported"
                              checked={!!formData.hrdfSupported}
                              onChange={(e) => setFormData({ ...formData, hrdfSupported: e.target.checked })}
                              className="w-5 h-5 rounded border-white/20 bg-white/10 text-blue-500 focus:ring-2 focus:ring-blue-500"
                            />
                            <label htmlFor="hrdf-supported" className="text-white text-sm">
                              {p('hrdf_support')}
                              <span className="block text-white/50 text-xs">{p('hrdf_support_hint')}</span>
                            </label>
                          </div>
                          <div className="md:col-span-3">
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_participants')}</label>
                            <div className="max-h-40 overflow-y-auto rounded-xl border border-white/20 bg-white/5 p-3 space-y-2">
                              {hrEmployees.length === 0 ? (
                                <p className="text-white/50 text-sm">{p('participants_empty', { defaultValue: 'لا يوجد موظفون — أضف موظفين من صفحة الموظفين' })}</p>
                              ) : hrEmployees.map((emp) => {
                                const label = formatHrEmployeeLabel(emp);
                                const checked = (formData.participants || []).includes(label);
                                return (
                                  <label key={emp.id || label} className="flex items-center gap-2 text-sm text-white/80 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={(e) => {
                                        const next = e.target.checked
                                          ? [...(formData.participants || []), label]
                                          : (formData.participants || []).filter((pName) => pName !== label);
                                        setFormData({ ...formData, participants: next });
                                      }}
                                      className="w-4 h-4 rounded border-white/20 bg-white/10"
                                    />
                                    <span>{label}{emp.mat ? ` (${emp.mat})` : ''}</span>
                                  </label>
                                );
                              })}
                            </div>
                            <p className="text-white/50 text-xs mt-1">{p('participants_selected', { count: formData.participants?.length ?? 0, defaultValue: '{{count}} مشارك' })}</p>
                          </div>
                    </div>
                  </>
                )}
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {/* View Modal - aligned with EmployeesPage view modal styling */}
      {showViewModal && viewingTraining && ReactDOM.createPortal(
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4"
            onClick={() => { setShowViewModal(false); setViewingTraining(null); }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
              style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth: '1100px' }}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">{p('view_modal_title')}</h2>
                <button
                  type="button"
                  onClick={() => { setShowViewModal(false); setViewingTraining(null); }}
                  className="text-white/70 hover:text-white transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              <div >
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <GraduationCap size={18} className="text-blue-400" />
                    {p('view_section_general')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-2">
                      <p className="text-white/60 text-sm mb-1">{p('export_col_title')}</p>
                      <p className="text-white font-medium text-lg">{viewingTraining.title || p('na')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('col_status')}</p>
                      <div className="flex">
                        <QawaemClickableStatusBadge status={viewingTraining.status} recordId={viewingTraining.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status')} />
                      </div>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('col_category')}</p>
                      <p className="text-white font-medium">{viewingTraining.category || p('na')}</p>
                    </div>
                    <div className="md:col-span-4">
                      <p className="text-white/60 text-sm mb-1">{p('col_description')}</p>
                      <p className="text-white font-medium whitespace-pre-wrap">
                        {viewingTraining.description?.trim() ? viewingTraining.description : p('na')}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <Calendar size={18} className="text-green-400" />
                    {p('view_section_trainer')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('col_trainer')}</p>
                      <p className="text-white font-medium">{viewingTraining.trainer || p('na')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('col_start_date')}</p>
                      <p className="text-white font-medium">
                        {viewingTraining.startDate
                          ? new Date(viewingTraining.startDate).toLocaleDateString(dateLocale)
                          : p('na')}
                      </p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('col_end_date')}</p>
                      <p className="text-white font-medium">
                        {viewingTraining.endDate
                          ? new Date(viewingTraining.endDate).toLocaleDateString(dateLocale)
                          : p('na')}
                      </p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('col_duration')}</p>
                      <p className="text-white font-medium">{viewingTraining.duration || p('na')}</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <DollarSign size={18} className="text-amber-400" />
                      {p('view_section_budget')}
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('col_cost')}</p>
                        <p className="text-white font-medium">
                          {viewingTraining.cost != null && viewingTraining.cost !== ''
                            ? formatBrandMoney(viewingTraining.cost)
                            : p('na')}
                        </p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('col_location')}</p>
                        <p className="text-white font-medium">{viewingTraining.location || p('na')}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <Users size={18} className="text-purple-400" />
                      {p('view_section_participants')}
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('col_participants')}</p>
                        <p className="text-white font-medium">{viewingTraining.participants?.length ?? 0}</p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('col_participants')}</p>
                        <p className="text-white font-medium">
                          {viewingTraining.participants?.length
                            ? viewingTraining.participants
                                .map((p) =>
                                  typeof p === 'object' && p !== null
                                    ? p.name || p.id || JSON.stringify(p)
                                    : String(p)
                                )
                                .join(', ')
                            : p('none_label')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {viewingTraining.notes ? (
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <FileText size={18} className="text-cyan-400" />
                      {p('label_notes')}
                    </h3>
                    <p className="text-white font-medium whitespace-pre-wrap">{viewingTraining.notes}</p>
                  </div>
                ) : null}
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </>
  );
};

export default TrainingPage;


